import { useState, useEffect } from 'react'
import { useAppDispatch } from '../../store/hooks'
import { createPatient } from '../../store/patientListSlice'
import { X, ChevronDown } from 'lucide-react'

interface AddPatientModalProps {
  open: boolean
  onClose: () => void
}

const speciesOptions = ['Dog', 'Cat', 'Bird', 'Reptile', 'Horse', 'Other'] as const
const sexOptions = ['Male', 'Female', 'Unknown'] as const

export function AddPatientModal({ open, onClose }: AddPatientModalProps) {
  const dispatch = useAppDispatch()

  const [name, setName] = useState('')
  const [species, setSpecies] = useState('')
  const [showSpeciesDropdown, setShowSpeciesDropdown] = useState(false)
  const [breed, setBreed] = useState('')
  const [dateOfBirth, setDateOfBirth] = useState('')
  const [weight, setWeight] = useState('')
  const [weightUnit, setWeightUnit] = useState<'lbs' | 'kg'>('lbs')
  const [sex, setSex] = useState('')
  const [showSexDropdown, setShowSexDropdown] = useState(false)
  const [spayNeuterStatus, setSpayNeuterStatus] = useState(false)
  const [microchipNumber, setMicrochipNumber] = useState('')
  const [allergies, setAllergies] = useState('')
  const [ownerName, setOwnerName] = useState('')
  const [ownerPhone, setOwnerPhone] = useState('')
  const [ownerEmail, setOwnerEmail] = useState('')
  const [notes, setNotes] = useState('')
  const [errors, setErrors] = useState<string[]>([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open) {
      setName('')
      setSpecies('')
      setShowSpeciesDropdown(false)
      setBreed('')
      setDateOfBirth('')
      setWeight('')
      setWeightUnit('lbs')
      setSex('')
      setShowSexDropdown(false)
      setSpayNeuterStatus(false)
      setMicrochipNumber('')
      setAllergies('')
      setOwnerName('')
      setOwnerPhone('')
      setOwnerEmail('')
      setNotes('')
      setErrors([])
      setSaving(false)
    }
  }, [open])

  const closeAllDropdowns = () => {
    setShowSpeciesDropdown(false)
    setShowSexDropdown(false)
  }

  const validate = (): string[] => {
    const errs: string[] = []
    if (!name.trim()) errs.push('Patient Name is required')
    if (!species) errs.push('Species is required')
    if (!ownerName.trim()) errs.push('Owner Name is required')
    return errs
  }

  const handleSave = async () => {
    const errs = validate()
    if (errs.length > 0) {
      setErrors(errs)
      return
    }
    setErrors([])
    setSaving(true)

    try {
      await dispatch(
        createPatient({
          name: name.trim(),
          species,
          breed: breed.trim(),
          date_of_birth: dateOfBirth || null,
          weight: weight ? parseFloat(weight) : null,
          weight_unit: weightUnit,
          sex: sex || 'Unknown',
          spay_neuter_status: spayNeuterStatus,
          microchip_number: microchipNumber.trim(),
          allergies: allergies.trim(),
          owner_name: ownerName.trim(),
          owner_phone: ownerPhone.trim(),
          owner_email: ownerEmail.trim(),
          notes: notes.trim(),
        })
      ).unwrap()
      onClose()
    } catch {
      setErrors(['Failed to create patient. Please try again.'])
    } finally {
      setSaving(false)
    }
  }

  if (!open) return null

  return (
    <div
      data-testid="add-patient-modal-overlay"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        data-testid="add-patient-modal"
        className="bg-surface rounded-[8px] w-full max-w-lg max-h-[85vh] overflow-y-auto"
        style={{ boxShadow: 'var(--shadow-elevation-2)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h2 className="text-[14px] font-semibold text-text-primary">Add Patient</h2>
          <button
            data-testid="add-patient-modal-close"
            onClick={onClose}
            className="flex items-center justify-center w-7 h-7 rounded-[4px] text-text-muted hover:bg-hover transition-colors duration-100 cursor-pointer"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-col gap-3 px-5 py-4">
          {/* Patient Name */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-text-muted">
              Patient Name <span className="text-status-error">*</span>
            </label>
            <input
              data-testid="add-patient-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter patient name..."
              className="w-full h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40"
            />
          </div>

          {/* Species */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-text-muted">
              Species <span className="text-status-error">*</span>
            </label>
            <div className="relative">
              <button
                type="button"
                data-testid="add-patient-species"
                onClick={() => {
                  closeAllDropdowns()
                  setShowSpeciesDropdown(!showSpeciesDropdown)
                }}
                className="w-full h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-left text-text-primary outline-none focus:ring-2 focus:ring-accent/40 cursor-pointer flex items-center justify-between"
              >
                {species || 'Select species...'}
                <ChevronDown size={12} className="text-text-muted" />
              </button>
              {showSpeciesDropdown && (
                <div
                  data-testid="add-patient-species-dropdown"
                  className="absolute z-10 top-full left-0 w-full mt-1 bg-surface border border-border rounded-[4px] max-h-48 overflow-y-auto"
                  style={{ boxShadow: 'var(--shadow-elevation-1)' }}
                >
                  {speciesOptions.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      data-testid={`add-species-option-${opt}`}
                      onClick={() => {
                        setSpecies(opt)
                        setShowSpeciesDropdown(false)
                      }}
                      className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Breed */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-text-muted">Breed</label>
            <input
              data-testid="add-patient-breed"
              type="text"
              value={breed}
              onChange={(e) => setBreed(e.target.value)}
              placeholder="Enter breed..."
              className="w-full h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40"
            />
          </div>

          {/* Date of Birth */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-text-muted">Date of Birth</label>
            <input
              data-testid="add-patient-dob"
              type="date"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              className="w-full h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40"
            />
          </div>

          {/* Weight + Unit */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-text-muted">Weight</label>
            <div className="flex items-center gap-2">
              <input
                data-testid="add-patient-weight"
                type="number"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                placeholder="0"
                className="flex-1 h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40"
              />
              <div className="flex items-center h-7 rounded-[4px] border border-border overflow-hidden">
                <button
                  type="button"
                  data-testid="add-patient-weight-lbs"
                  onClick={() => setWeightUnit('lbs')}
                  className={`h-full px-2 text-[12px] transition-colors duration-100 cursor-pointer ${
                    weightUnit === 'lbs'
                      ? 'bg-accent text-white'
                      : 'bg-base text-text-secondary hover:bg-hover'
                  }`}
                >
                  lbs
                </button>
                <button
                  type="button"
                  data-testid="add-patient-weight-kg"
                  onClick={() => setWeightUnit('kg')}
                  className={`h-full px-2 text-[12px] transition-colors duration-100 cursor-pointer ${
                    weightUnit === 'kg'
                      ? 'bg-accent text-white'
                      : 'bg-base text-text-secondary hover:bg-hover'
                  }`}
                >
                  kg
                </button>
              </div>
            </div>
          </div>

          {/* Sex */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-text-muted">Sex</label>
            <div className="relative">
              <button
                type="button"
                data-testid="add-patient-sex"
                onClick={() => {
                  closeAllDropdowns()
                  setShowSexDropdown(!showSexDropdown)
                }}
                className="w-full h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-left text-text-primary outline-none focus:ring-2 focus:ring-accent/40 cursor-pointer flex items-center justify-between"
              >
                {sex || 'Select sex...'}
                <ChevronDown size={12} className="text-text-muted" />
              </button>
              {showSexDropdown && (
                <div
                  data-testid="add-patient-sex-dropdown"
                  className="absolute z-10 top-full left-0 w-full mt-1 bg-surface border border-border rounded-[4px] max-h-40 overflow-y-auto"
                  style={{ boxShadow: 'var(--shadow-elevation-1)' }}
                >
                  {sexOptions.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      data-testid={`add-sex-option-${opt}`}
                      onClick={() => {
                        setSex(opt)
                        setShowSexDropdown(false)
                      }}
                      className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Spay/Neuter Status */}
          <div className="flex items-center gap-2">
            <input
              data-testid="add-patient-spay-neuter"
              type="checkbox"
              checked={spayNeuterStatus}
              onChange={(e) => setSpayNeuterStatus(e.target.checked)}
              className="w-4 h-4 rounded border-border accent-accent"
            />
            <label className="text-[13px] text-text-primary">Spayed/Neutered</label>
          </div>

          {/* Microchip Number */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-text-muted">Microchip Number</label>
            <input
              data-testid="add-patient-microchip"
              type="text"
              value={microchipNumber}
              onChange={(e) => setMicrochipNumber(e.target.value)}
              placeholder="Enter microchip number..."
              className="w-full h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40"
            />
          </div>

          {/* Allergies */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-text-muted">Allergies</label>
            <textarea
              data-testid="add-patient-allergies"
              value={allergies}
              onChange={(e) => setAllergies(e.target.value)}
              placeholder="Comma-separated allergies..."
              rows={2}
              className="px-2 py-1 rounded-[4px] border border-border bg-base text-[13px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40 resize-none"
            />
          </div>

          {/* Owner Name */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-text-muted">
              Owner Name <span className="text-status-error">*</span>
            </label>
            <input
              data-testid="add-patient-owner-name"
              type="text"
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
              placeholder="Enter owner name..."
              className="w-full h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40"
            />
          </div>

          {/* Owner Phone */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-text-muted">Owner Phone</label>
            <input
              data-testid="add-patient-owner-phone"
              type="text"
              value={ownerPhone}
              onChange={(e) => setOwnerPhone(e.target.value)}
              placeholder="Enter phone number..."
              className="w-full h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40"
            />
          </div>

          {/* Owner Email */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-text-muted">Owner Email</label>
            <input
              data-testid="add-patient-owner-email"
              type="text"
              value={ownerEmail}
              onChange={(e) => setOwnerEmail(e.target.value)}
              placeholder="Enter email..."
              className="w-full h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40"
            />
          </div>

          {/* Notes */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-text-muted">Notes</label>
            <textarea
              data-testid="add-patient-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes..."
              rows={2}
              className="px-2 py-1 rounded-[4px] border border-border bg-base text-[13px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40 resize-none"
            />
          </div>

          {/* Errors */}
          {errors.length > 0 && (
            <div data-testid="add-patient-errors" className="text-[11px] text-status-error">
              {errors.map((err, i) => (
                <p key={i}>{err}</p>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border">
          <button
            data-testid="add-patient-cancel"
            onClick={onClose}
            className="inline-flex items-center h-7 px-3 rounded-[4px] text-[13px] text-text-secondary hover:bg-hover transition-colors duration-100 cursor-pointer"
          >
            Cancel
          </button>
          <button
            data-testid="add-patient-save"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center h-7 px-3 rounded-[4px] bg-accent text-white text-[13px] hover:bg-accent-dark transition-colors duration-100 cursor-pointer disabled:opacity-50 disabled:cursor-default"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}
